import {
	Button,
	Group,
	Modal,
	PasswordInput,
	Select,
	Stack,
	Switch,
	TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import type { User } from "../../store/users/usersSlice";

const ROLE_OPTIONS = [
	{ value: "admin", label: "Admin" },
	{ value: "manager", label: "Manager" },
	{ value: "viewer", label: "Viewer" },
];

interface UserFormData {
	username: string;
	email: string;
	password?: string;
	role: string;
	isActive: boolean;
}

interface UserModalProps {
	opened: boolean;
	user: User | null;
	loading: boolean;
	onClose: () => void;
	onSubmit: (data: UserFormData) => void;
}

function UserModal({
	opened,
	user,
	loading,
	onClose,
	onSubmit,
}: UserModalProps) {
	const form = useForm<UserFormData>({
		initialValues: {
			username: "",
			email: "",
			password: "",
			role: "viewer",
			isActive: true,
		},
		validate: {
			username: (value) => (!value ? "Username is required" : null),
			email: (value) => {
				if (!value) return "Email is required";
				return /^\S+@\S+$/.test(value) ? null : "Invalid email format";
			},
			password: (value) => {
				if (!user && !value) return "Password is required";
				if (value && value.length < 6)
					return "Password must be at least 6 characters";
				return null;
			},
			role: (value) => (!value ? "Role is required" : null),
		},
	});

	useEffect(() => {
		if (user) {
			form.setValues({
				username: user.username,
				email: user.email,
				password: "",
				role: user.role,
				isActive: user.isActive,
			});
		} else if (opened) {
			form.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?._id, opened]);

	const handleSubmit = (values: UserFormData) => {
		onSubmit(values);
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={user ? "Edit User" : "Create User"}
			size="md">
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="md">
					<TextInput
						label="Username"
						placeholder="Enter username"
						required
						{...form.getInputProps("username")}
					/>

					<TextInput
						label="Email"
						placeholder="user@example.com"
						required
						{...form.getInputProps("email")}
					/>

					{!user && (
						<PasswordInput
							label="Password"
							placeholder="Enter password"
							required
							{...form.getInputProps("password")}
						/>
					)}

					<Select
						label="Role"
						placeholder="Select role"
						required
						data={ROLE_OPTIONS}
						{...form.getInputProps("role")}
					/>

					{user && (
						<Switch
							label="Active"
							description="User can log in and access the system"
							{...form.getInputProps("isActive", { type: "checkbox" })}
						/>
					)}

					<Group
						justify="flex-end"
						mt="lg">
						<Button
							variant="light"
							onClick={onClose}>
							Cancel
						</Button>
						<Button
							type="submit"
							loading={loading}>
							{user ? "Update" : "Create"}
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}

export default UserModal;
export type { UserFormData };
